import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';

const API = `${API_URL}/api/browser-assistance`;
const options = (token, signal) => ({ headers: { Authorization: `Bearer ${token}` }, withCredentials: true, signal, timeout: 10000 });
const openSettings = () => window.location.assign('/?page=config&tab=navegador');
const button = 'rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold disabled:opacity-50';

function usePending(token) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    let stopped = false, timer;
    const controller = new AbortController();
    async function poll() {
      if (document.hidden || stopped) return;
      try {
        const { data } = await axios.get(API, options(token, controller.signal));
        if (!stopped) { setItems(data.items || []); setError(false); }
      } catch (e) { if (!stopped) { setItems([]); setError(true); } }
      if (!stopped) timer = setTimeout(poll, 5000);
    }
    function visible() { clearTimeout(timer); if (document.hidden) setItems([]); else poll(); }
    poll(); document.addEventListener('visibilitychange', visible);
    return () => { stopped = true; clearTimeout(timer); controller.abort(); document.removeEventListener('visibilitychange', visible); };
  }, [token]);
  return { items, error };
}

export function BrowserAssistanceNotice({ token }) {
  const { items } = usePending(token);
  const seen = useRef(new Set());
  useEffect(() => {
    const fresh = items.filter(item => !seen.current.has(item.id));
    items.forEach(item => seen.current.add(item.id));
    if (fresh.length) toast('O navegador precisa de você', {
      id: 'browser-assistance', description: 'Há uma verificação aguardando em Configurações.',
      action: { label: 'Abrir', onClick: openSettings }, duration: 10000,
    });
  }, [items]);
  if (!items.length) return null;
  return <button type="button" onClick={openSettings} className="flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-900" aria-label={`${items.length} validações do navegador pendentes`}>
    <ShieldCheck className="h-5 w-5" /><span className="hidden sm:inline">Validação pendente</span><span>{items.length}</span>
  </button>;
}

function Challenge({ token, id, onClose }) {
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState('Conectando ao navegador…');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(null);
  const sending = useRef(false);
  const alive = useRef(true);
  const notBefore = useRef(0);
  useEffect(() => {
    alive.current = true;
    let timer, generation = 0;
    const controller = new AbortController();
    async function poll() {
      if (!alive.current || document.hidden) return;
      const current = generation;
      try {
        const { data } = await axios.get(`${API}/${id}`, options(token, controller.signal));
        if (!alive.current || document.hidden || current !== generation) return;
        setSession(data);
        setMessage({ verified: 'Validação aceita. A atividade pode continuar.', expired: 'Esta solicitação expirou.', cancelled: 'Validação interrompida.', unavailable: 'Esta verificação precisa de atendimento no navegador.' }[data.status] || (data.connected ? 'Clique nas opções do CAPTCHA abaixo.' : 'Aguardando conexão com o navegador…'));
        if (!sending.current && Date.now() >= notBefore.current) setBusy(false);
        if (data.status !== 'waiting') return;
      } catch (e) {
        if (!alive.current || current !== generation) return;
        setSession(null); setLoaded(null); setMessage('Não foi possível atualizar. Aguardando conexão…');
      }
      if (alive.current) timer = setTimeout(poll, 1200);
    }
    function visible() { generation++; clearTimeout(timer); setSession(null); setLoaded(null); if (!document.hidden) poll(); }
    poll(); document.addEventListener('visibilitychange', visible);
    return () => { alive.current = false; controller.abort(); clearTimeout(timer); document.removeEventListener('visibilitychange', visible); };
  }, [token, id]);

  async function send(action, position = {}) {
    if (sending.current || busy || document.hidden || session?.status !== 'waiting') return;
    sending.current = true; setBusy(true); notBefore.current = Date.now() + 1800;
    try {
      await axios.post(`${API}/${id}/input`, { id: crypto.randomUUID(), action, frame_id: session.frame?.frame_id || '', ...position }, options(token));
      if (alive.current) setMessage(action === 'cancel' ? 'Solicitando interrupção…' : 'Enviado. Aguarde a atualização do site.');
    } catch (e) {
      if (alive.current) { setSession(null); setLoaded(null); setMessage('A tela mudou ou a conexão falhou. Aguarde a atualização antes de tentar novamente.'); }
    } finally { sending.current = false; }
  }
  const frame = session?.status === 'waiting' && session.connected ? session.frame : null;
  function tap(event) {
    if (!frame || loaded !== frame.frame_id || busy) return;
    const box = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - box.left) * frame.width / box.width;
    const y = (event.clientY - box.top) * frame.height / box.height;
    if (x >= 0 && y >= 0 && x < frame.width && y < frame.height) send('tap', { x, y });
  }
  return <Dialog open onOpenChange={open => !open && onClose()}>
    <DialogContent className="max-h-[95dvh] max-w-[700px] overflow-y-auto" data-testid="browser-challenge">
      <DialogTitle>Validação no navegador</DialogTitle>
      <DialogDescription>Resolva a verificação de {session?.site || 'segurança'}. O navegador aguarda sua resposta.</DialogDescription>
      <p role="status" className="text-sm text-slate-700">{message}</p>
      {frame && <div className="flex justify-center overflow-hidden rounded-xl border bg-white p-2">
        <img src={`data:image/png;base64,${frame.png}`} width={frame.width} height={frame.height}
          alt="CAPTCHA interativo: clique diretamente nas opções exibidas" draggable={false}
          onLoad={() => setLoaded(frame.frame_id)} onClick={tap}
          style={{ width: frame.width, maxWidth: '100%', height: 'auto', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.65 : 1 }}
          data-testid="captcha-crop" />
      </div>}
      {session?.status === 'waiting' && <div className="flex flex-wrap gap-2">
        <button type="button" className={button} disabled={busy || !session.connected} onClick={() => send('check')}>Verificar resposta</button>
        <button type="button" className={button} disabled={busy} onClick={() => send('cancel')}>Interromper validação</button>
      </div>}
      <p className="text-xs text-slate-500">Somente o recorte da verificação é exibido. Fechar esta janela mantém a atividade aguardando. Desafios que exigem digitação, áudio ou arraste precisam de atendimento no navegador.</p>
    </DialogContent>
  </Dialog>;
}

export default function BrowserAssistance({ token }) {
  const { items, error } = usePending(token);
  const [selected, setSelected] = useState(null);
  return <section className="space-y-4 rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm" data-testid="browser-assistance-settings">
    <h2 className="text-xl font-semibold text-slate-900">Validação no navegador</h2>
    <p className="text-sm text-slate-500">Quando uma atividade precisar de CAPTCHA, abra a solicitação e resolva apenas o recorte da verificação. Você também pode atender pelo aplicativo LBCC.</p>
    {error ? <p role="status">Não foi possível consultar as solicitações. Tentando novamente…</p> : !items.length ? <p role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Nenhuma validação aguardando no momento.</p> : items.map(item => <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4">
      <div><p className="font-semibold">{item.site}</p><p className="text-sm text-slate-500">O navegador está aguardando você.</p></div>
      <button type="button" className={`${button} bg-slate-900 text-white`} onClick={() => setSelected(item.id)}>Resolver verificação</button>
    </div>)}
    {selected && <Challenge key={selected} token={token} id={selected} onClose={() => setSelected(null)} />}
  </section>;
}
